import I18n from 'I18n';
import { produce } from 'immer';
import { createImmutableObjectClass } from 'immutable-less/ImmutableObject';

import { Industry as MarketplaceIndustry } from 'marketplace-ui-common/types/industry';

import Reply, { ReplyJSON } from './Reply';
import {
  CompanySize,
  ReviewQuestion,
  ReviewTag,
  RatingAxis,
  ReviewType,
} from '../types';
import * as ReviewTypes from '../constants/ReviewType';
import { RatingAxes, AppQualityRatingsOrder } from '../constants/RatingAxes';
import { PREFER_NOT_TO_SAY } from '../constants/CompanySize';

export type AnswersJSONType = Partial<Record<ReviewQuestion, string | null>>;

export type AnswersRecordType = Map<Partial<ReviewQuestion>, string | null>;

export type TagsJSONType = Partial<Record<ReviewTag, any[]>>;

export type TagsRecordType = Map<Partial<ReviewTag>, any[]>;

interface Attribute {
  [index: string]: any;
}

export interface ReviewResponseJSON {
  answers: AnswersJSONType;
  attributes: Attribute[];
  avatarUrl: string | null;
  companyName: string | null;
  content: Record<any, any>;
  createdAt: number | null;
  employees: CompanySize | null; // create endpoint specific
  id: number | null;
  lastEditedAt: number | null;
  marketplaceIndustry: MarketplaceIndustry | null; // create endpoint specific
  offeringVersion: number | null;
  original: {
    answers: AnswersJSONType;
    ratings: Record<RatingAxis, number>;
    tags: TagsJSONType;
  } | null;
  originalLanguage: string | null;
  ratings: Record<RatingAxis, number>;
  replies: ReplyJSON[] | null;
  reply: ReplyJSON | null;
  reviewerData: Record<string, any>; // search endpoints - read only, converted to marketplaceIndustry and employees in review.fromJSON()
  reviewerDisplayName: string | null;
  reviewType: ReviewType | null;
  showAvatar: boolean;
  tags: TagsJSONType;
  translated: boolean;
  untranslated: {
    answers: AnswersJSONType;
    reply: string | null;
    replies: string[] | null;
  } | null;
}

interface ReviewRecord {
  answers: AnswersRecordType;
  archived: boolean;
  archivedReplies: Reply[];
  attributes: Map<string, any>[];
  avatarUrl: string | null;
  companyName: string | null; // Solutions Marketplace only TODO deprecate
  content: Map<any, any>;
  createdAt: number | null;
  employees: CompanySize | null;
  entityId: number | null;
  id: number | null;
  lastEditedAt: number | null;
  listingName: string | null; // Admin endpoint only
  marketplaceIndustry: MarketplaceIndustry | null;
  offeringType: string | null;
  offeringVersion: number | null; // Asset Marketplace only
  original: {
    answers: AnswersRecordType;
    ratings: Map<RatingAxis, number>;
    tags: TagsRecordType;
  };
  originalLanguage: string | null;
  portalId: number | null; // Customer endpoint only
  providerPortalId: number | null; // Admin endpoint only
  ratings: Map<RatingAxis, number>;
  replies: Reply[];
  reply: Reply; // Use Reply record
  reviewerDisplayName: string | null;
  reviewType: ReviewType | null;
  showAvatar: boolean | true;
  tags: TagsRecordType;
  translated: boolean;
  userId: number | null; // Customer endpoint only
  untranslated: {
    answers: AnswersRecordType;
    reply: string | null;
    replies: string[] | null;
  } | null;
}

const reviewResponseDefaults: ReviewResponseJSON = {
  answers: {},
  attributes: [],
  avatarUrl: null,
  companyName: null,
  content: {},
  createdAt: null,
  employees: null,
  id: null,
  lastEditedAt: null,
  marketplaceIndustry: null,
  offeringVersion: null,
  original: null,
  originalLanguage: null,
  ratings: {},
  replies: [],
  reply: {
    id: null,
    repliedAt: null,
    reply: null,
    replierDisplayName: null,
    avatarUrl: null,
    companyName: null,
  },
  reviewerData: {},
  reviewerDisplayName: null,
  reviewType: null,
  showAvatar: true,
  tags: {},
  translated: false,
  untranslated: null,
};

export default class Review extends createImmutableObjectClass<
  ReviewResponseJSON,
  ReviewRecord
>(reviewResponseDefaults, {
  fromJSON: ({
    answers,
    attributes,
    content: __content,
    employees,
    marketplaceIndustry,
    original,
    ratings,
    replies,
    reply,
    reviewerData,
    tags,
    untranslated,
    ...rest
  }) => {
    const reviewerDataEmployees =
      (reviewerData && reviewerData.employees) || null;
    const reviewerDataMarketplaceIndustry =
      (reviewerData && reviewerData.industry) || null;

    return {
      answers: new Map(Object.entries(answers)) as AnswersRecordType,
      archived: false,
      archivedReplies: [],
      attributes: attributes.map((attr) => new Map(Object.entries(attr))),
      content: new Map(),
      employees: reviewerDataEmployees || employees,
      entityId: null,
      listingName: null,
      marketplaceIndustry:
        reviewerDataMarketplaceIndustry || marketplaceIndustry,
      offeringType: null,
      original: {
        answers: original
          ? (new Map(Object.entries(original.answers)) as AnswersRecordType)
          : new Map(),
        ratings: original
          ? new Map(Object.entries(original.ratings))
          : new Map(),
        tags: original
          ? (new Map(Object.entries(original.tags)) as TagsRecordType)
          : new Map(),
      },
      portalId: reviewerData.portalId || null,
      providerPortalId: null,
      ratings: new Map(Object.entries(ratings)),
      replies: replies ? replies.map((rep) => new Reply(rep)) : [],
      reply: reply ? new Reply(reply) : new Reply(),
      tags: new Map(Object.entries(tags)) as TagsRecordType,
      userId: null,
      untranslated: untranslated
        ? {
            answers: new Map(
              Object.entries(untranslated.answers)
            ) as AnswersRecordType,
            reply: untranslated.reply,
            replies: untranslated.replies,
          }
        : null,
      ...rest,
    };
  },
  toJSON: ({
    answers,
    attributes,
    content: __content,
    original: __original,
    ratings,
    reply,
    tags,
    untranslated,
    ...rest
  }) => ({
    answers: Object.fromEntries(answers) as AnswersJSONType,
    attributes: attributes.map((attr) => Object.fromEntries(attr)),
    content: {},
    original: null,
    ratings: Object.fromEntries(ratings),
    reply: reply.toJSON(),
    reviewerData: {},
    tags: Object.fromEntries(tags) as TagsJSONType,
    untranslated: untranslated
      ? {
          answers: Object.fromEntries(untranslated.answers) as AnswersJSONType,
          reply: untranslated.reply,
          replies: untranslated.replies,
        }
      : null,
    ...rest,
  }),
}) {
  static fromArray(data: ReviewResponseJSON[], reviewType: ReviewType) {
    return data.map(
      (review) =>
        new Review({
          ...review,
          reviewType,
        })
    );
  }

  static toEmployeeString(range: CompanySize) {
    if (!range) {
      return '';
    }
    let rangeKey;
    let formattedMin;
    let formattedMax;
    if (range === PREFER_NOT_TO_SAY) {
      rangeKey = 'undisclosed';
    } else {
      const [__range, min, max] = range.split('_');
      formattedMin = I18n.formatNumber(parseInt(min, 10));
      formattedMax = I18n.formatNumber(parseInt(max, 10));
      rangeKey = min === '1' ? 'single' : max ? 'range' : 'indefinite';
    }
    return I18n.text(`marketplace-ui-reviews.employees.${rangeKey}`, {
      min: formattedMin,
      max: formattedMax,
    });
  }

  getHelpfulCount() {
    return this.get('attributes').filter((attr) => attr.get('helpful')).length;
  }

  toggleHelpful(reviewId: number, userId: number, helpful: boolean) {
    return produce(this, (draftReview) => {
      const indexToUpdate = draftReview
        .get('attributes')
        .findIndex((attr) => attr.get('userId') === userId);

      const newAttributes = draftReview.attributes;
      if (indexToUpdate !== -1) {
        newAttributes[indexToUpdate].set('helpful', helpful);
      } else {
        newAttributes.push(
          new Map<string, any>([
            ['reviewId', reviewId],
            ['userId', userId],
            ['helpful', helpful],
          ])
        );
      }
      draftReview.set('attributes', newAttributes);
    });
  }

  getQualityRatings(showOriginal?: boolean) {
    const initialMap = showOriginal ? this.original.ratings : this.ratings;
    const qualityRatingsMap = produce(initialMap, (ratingsDraft) => {
      ratingsDraft.delete(RatingAxes.OVERALL);
    });
    switch (this.reviewType) {
      case ReviewTypes.APP:
        return AppQualityRatingsOrder.reduce(
          (final: Map<RatingAxis, number | undefined>, key: RatingAxis) => {
            return final.set(key, initialMap.get(key));
          },
          new Map<RatingAxis, number>()
        );
      default:
        return qualityRatingsMap;
    }
  }

  getReplies(showUntranslatedText?: boolean) {
    return this.get('replies').map((reply, index) => {
      return showUntranslatedText
        ? new Reply({
            ...reply,
            reply: this.get('untranslated')?.replies?.[index] || reply.reply,
          })
        : reply;
    });
  }

  getReply(showUntranslatedText?: boolean) {
    return showUntranslatedText
      ? new Reply({
          ...this.get('reply'),
          reply: this.get('untranslated')?.reply || this.get('reply')?.reply,
        })
      : this.get('reply');
  }

  setOverallRating(overallRating: number) {
    return produce(this, (reviewDraft) => {
      reviewDraft.get('ratings').set(RatingAxes.OVERALL, overallRating);
    });
  }

  setEmployees(employeeSize: CompanySize) {
    return produce(this, (reviewDraft) => {
      reviewDraft.set('employees', employeeSize);
    });
  }

  setIndustry(industry: MarketplaceIndustry) {
    return produce(this, (reviewDraft) => {
      reviewDraft.set('marketplaceIndustry', industry);
    });
  }

  hasContent() {
    return this.answers.size > 0;
  }

  isTranslated() {
    return this.translated || false;
  }

  updateReview(updates: Partial<ReviewRecord>) {
    return produce(this, (reviewDraft) => {
      Object.entries(updates).forEach(([key, value]) => {
        if (key === 'answers') {
          (value! as Map<ReviewQuestion, any>).forEach(
            (updateValue, updateKey) => {
              if (updateValue === '') {
                reviewDraft.get(key).delete(updateKey);
              } else {
                reviewDraft.get(key).set(updateKey, updateValue);
              }
            }
          );
        } else if (key === 'tags') {
          (value! as Map<ReviewTag, any>).forEach((updateValue, updateKey) => {
            reviewDraft.get(key).set(updateKey, updateValue);
          });
        } else if (key === 'ratings') {
          (value! as Map<string, any>).forEach((updateValue, updateKey) => {
            reviewDraft.get(key).set(updateKey, updateValue);
          });
        } else {
          reviewDraft.set(key as keyof ReviewRecord, value);
        }
      });
    });
  }
}
